import { useState } from 'react';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import { PageHeader } from '@/components/brand/PageHeader';
import { Panel } from '@/components/brand/Panel';
import { useApiQuery } from '@/lib/hooks';
import { api, ApiError, type CostsSummary, type CostsByAutomation } from '@/lib/api';
import { EmptyState, ErrorState, LoadingState, UnauthorizedState } from '@/components/brand/States';

import { thirtyDayWindow } from '@/lib/time';

export function CostsPage() {
  const { from, to } = thirtyDayWindow();
  const [drillSource, setDrillSource] = useState<string | null>(null);

  const q = useApiQuery<CostsSummary>({
    queryKey: ['costs/summary', from, to],
    queryFn: () => api.costsSummary({ from, to }),
  });

  const drillQ = useApiQuery<CostsByAutomation>({
    enabled: drillSource !== null,
    queryKey: ['costs/by-automation', drillSource, from, to],
    queryFn: () => api.costsByAutomation({ source: drillSource!, from, to }),
  });

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Spend" title="Costs" meta="Last 30 days · GBP canonical" />
      <Panel title="Month-to-date">
        {q.isLoading && <LoadingState />}
        {q.error instanceof ApiError && q.error.isUnauthorized() && <UnauthorizedState />}
        {q.error && !(q.error instanceof ApiError && q.error.isUnauthorized()) && (
          <ErrorState title="Couldn't load cost data" detail={q.error.message} />
        )}
        {q.data && q.data.total_gbp === 0 && (
          <EmptyState
            title="No spend recorded yet"
            hint="Anthropic-call events from internal Workers feed this view. Spend appears once those Workers emit api_call_completed events with cost_usd / cost_gbp."
          />
        )}
        {q.data && q.data.total_gbp > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="eyebrow mb-1">Total</p>
              <p className="num text-h2">£{q.data.total_gbp.toFixed(2)}</p>
            </div>
            <div className="md:col-span-2">
              <p className="eyebrow mb-2">Per service · click to drill in</p>
              <ul className="divide-y divide-rule">
                {q.data.per_service.map((s) => (
                  <li key={s.service}>
                    <button
                      type="button"
                      onClick={() => setDrillSource(drillSource === s.service ? null : s.service)}
                      className={`w-full flex justify-between py-2 text-small text-left hover:bg-bg-section/30 ${drillSource === s.service ? 'text-fg-default font-bold' : 'text-fg-default'}`}
                    >
                      <span className="mono">{s.service}</span>
                      <span className="num">£{s.gbp.toFixed(2)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Panel>

      {drillSource && (
        <Panel title={`Drill-down · ${drillSource}`} subtitle="Per automation">
          {drillQ.isLoading && <LoadingState />}
          {drillQ.error && <ErrorState title="Couldn't load drill-down" detail={(drillQ.error as Error).message} />}
          {drillQ.data && drillQ.data.items.length === 0 && (
            <EmptyState title={`No costed events for ${drillSource}`} />
          )}
          {drillQ.data && drillQ.data.items.length > 0 && (
            <table className="w-full text-small">
              <thead className="text-fg-muted text-eyebrow">
                <tr>
                  <th className="text-left py-2">Automation</th>
                  <th className="text-right">Events</th>
                  <th className="text-right">£</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-rule">
                {drillQ.data.items.map((row) => (
                  <tr key={`${row.automation_id ?? 'null'}-${row.automation_name ?? 'null'}`}>
                    <td className="py-2 mono">
                      {row.automation_name ?? row.automation_id ?? <span className="text-fg-muted">— unattributed</span>}
                    </td>
                    <td className="text-right num">{row.events}</td>
                    <td className="text-right num">£{row.gbp.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      {q.data && q.data.per_day.length > 1 && (
        <Panel title="Daily trend" subtitle="GBP per UTC day">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={q.data.per_day} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
              <XAxis dataKey="day" stroke="#6E6A6C" tick={{ fontSize: 11 }} />
              <YAxis stroke="#6E6A6C" tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: '#F8F4F0', border: '1px solid rgba(47,41,46,0.18)', fontSize: 12 }}
                formatter={(v: number) => [`£${v.toFixed(2)}`, 'GBP']}
              />
              <Line type="monotone" dataKey="gbp" stroke="#78953C" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      )}
    </div>
  );
}
